import React, { useState } from "react";
import {
  Dialog,
  Container,
  Typography,
  Button,
  Grid,
  Box,
  Chip,
  alpha,
  Card,
  CardMedia,
  CardContent,
  Dialog as ImageDialog,
  IconButton,
} from "@mui/material";
import { RecordCircle, Receipt1, DocumentText1, CloseCircle, DocumentDownload } from "iconsax-react";
import styles from "./ticketDetailsDialogStyles";

function StatusChip({ status }) {
  const statusConfig = {
    "Pendiente": { color: "#FFA726", label: "Pendiente" },
    "En Proceso": { color: "#FFA726", label: "En Proceso" },
    "Procesando": { color: "#FFA726", label: "Procesando" },
    "procesando": { color: "#FFA726", label: "Procesando" },
    "Terminado": { color: "#53CA98", label: "Terminado" },
    "terminado": { color: "#53CA98", label: "Terminado" },
    "registrado": { color: "#42A5F5", label: "Registrado" },
    "Resuelto": { color: "#53CA98", label: "Resuelto" },
    "Cerrado": { color: "#78909C", label: "Cerrado" },
    "Cancelado": { color: "#DB463C", label: "Cancelado" },
  };

  const config = statusConfig[status] || { color: "#78909C", label: status || "N/A" };

  return (
    <Chip
      sx={{
        color: config.color,
        backgroundColor: alpha(config.color, 0.1),
      }}
      icon={<RecordCircle size="12" variant="Bold" />}
      label={config.label}
      size="small"
    />
  );
}

const TicketDetailsDialog = ({ open, onClose, ticket }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    onClose();
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setImageDialogOpen(true);
  };

  const handleImageDialogClose = () => {
    setImageDialogOpen(false);
    setSelectedImage(null);
  };

  const handleDownloadImage = async (imageURL, fileName) => {
    window.open(imageURL, '_blank');
  };

  const handlePdfClick = (pdfUrl) => {
    window.open(pdfUrl, "_blank");
  };

  const getImageLabel = (key) => {
    const labels = {
      evidence_image: "Evidencia",
      front_image: "Imagen Frontal",
      package_length_image: "Imagen de Longitud",
      package_width_image: "Imagen de Ancho",
      package_weight_image: "Imagen de Peso",
      package_guide_image: "Imagen de Guía",
      image_invoice_content: "Factura (PDF)",
    };
    return labels[key] || key;
  };

  const hasImages = ticket?.images && Object.values(ticket.images).some((url) => url !== null);

  if (!ticket) return null;

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      PaperProps={{ style: styles.dialog }}
      maxWidth="md"
      fullWidth
    >
      <Container style={styles.mainContainer}>
        <Typography component="div" variant="h5" style={styles.title}>
          Información del Ticket
        </Typography>



        <Box style={styles.ticketUserInfoContainer}>
          <Box style={styles.ticketUserInfoRight}>
            <Box style={styles.ticketUserInfoLeftContainer}>
              <Box style={styles.receiptIconContainer}>
                <Receipt1 variant="Bold" size={24} style={styles.receiptIcon} />
              </Box>
              <Box style={styles.ticketUserInfoLeft}>
                <Typography style={styles.nameLabel}>{ticket.clientName}</Typography>
                <Typography style={styles.typeUserLabel}>{ticket.clientEmail || "N/A"}</Typography>
              </Box>
            </Box>
            <Box style={styles.statusContainer}>
              <StatusChip status={ticket.status} />
            </Box>
          </Box>
        </Box>

        <Box style={styles.ticketMainInfoContainer}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={4}>
              <Box style={styles.infoSection}>
                <Typography style={styles.infoLabel}>Número de Reporte</Typography>
                <Typography style={styles.infoValue}>{ticket.id}</Typography>
              </Box>
            </Grid>

            {ticket.reportType !== 'Cambio de domicilio' && ticket.reportType !== 'Créditos' && ticket.reportType !== 'Página web' && (
              <>
                <Grid item xs={12} sm={4}>
                  <Box style={styles.infoSection}>
                    <Typography style={styles.infoLabel}>Número de Guía</Typography>
                    <Typography style={styles.infoValue}>{ticket.guideNumber}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box style={styles.infoSection}>
                    <Typography style={styles.infoLabel}>Proveedor</Typography>
                    <Typography style={styles.infoValue}>{ticket.provider}</Typography>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </Box>



        <Grid container spacing={1}>

          <Grid item xs={12} sm={4}>
            <Box style={styles.infoSection}>
              <Typography style={styles.infoLabel}>Tipo de Reporte</Typography>
              <Typography style={styles.infoValue}>{ticket.reportType}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box style={styles.infoSection}>
              <Typography style={styles.infoLabel}>Fecha de Reporte</Typography>
              <Typography style={styles.infoValue}>{ticket.reportDate}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box style={styles.infoSection}>
              <Typography style={styles.infoLabel}>Última Actualización</Typography>
              <Typography style={styles.infoValue}>{ticket.lastUpdateDate}</Typography>
            </Box>
          </Grid>
        </Grid>

        {ticket.address && ticket.reportType.trim() !== 'Créditos' && ticket.reportType.trim() !== 'Página web' && ticket.reportType.trim() !== 'Reembolso' && ticket.reportType.trim() !== 'Retiro de efectivo' && ticket.reportType.trim() !== 'Servicio al cliente' && ticket.reportType.trim() && (
          <Box style={styles.ticketMainInfoContainer}>
            <Typography style={styles.imagesSectionLabel}>Información de Dirección</Typography>
            <Grid container spacing={1} style={{ marginTop: 8 }}>
              <Grid item xs={12} sm={6}>
                <Box style={styles.infoSection}>
                  <Typography style={styles.infoLabel}>Calle</Typography>
                  <Typography style={styles.infoValue}>{ticket.address.street || ""}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box style={styles.infoSection}>
                  <Typography style={styles.infoLabel}>Número Exterior</Typography>
                  <Typography style={styles.infoValue}>{ticket.address.external_number || "N/A"}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box style={styles.infoSection}>
                  <Typography style={styles.infoLabel}>Número Interior</Typography>
                  <Typography style={styles.infoValue}>{ticket.address.internal_number || "N/A"}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box style={styles.infoSection}>
                  <Typography style={styles.infoLabel}>Colonia</Typography>
                  <Typography style={styles.infoValue}>{ticket.address.colony || "N/A"}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box style={styles.infoSection}>
                  <Typography style={styles.infoLabel}>Código Postal</Typography>
                  <Typography style={styles.infoValue}>{ticket.address.postal_code || "N/A"}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box style={styles.infoSection}>
                  <Typography style={styles.infoLabel}>Ciudad</Typography>
                  <Typography style={styles.infoValue}>{ticket.address.city || "N/A"}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box style={styles.infoSection}>
                  <Typography style={styles.infoLabel}>Estado</Typography>
                  <Typography style={styles.infoValue}>{ticket.address.state || "N/A"}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box style={styles.infoSection}>
                  <Typography style={styles.infoLabel}>Teléfono</Typography>
                  <Typography style={styles.infoValue}>{ticket.address.phone || "N/A"}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box style={styles.infoSection}>
                  <Typography style={styles.infoLabel}>Email</Typography>
                  <Typography style={styles.infoValue}>{ticket.address.email || "N/A"}</Typography>
                </Box>
              </Grid>
              {ticket.address.between_streets && (
                <Grid item xs={12}>
                  <Box style={styles.infoSection}>
                    <Typography style={styles.infoLabel}>Entre Calles</Typography>
                    <Typography style={styles.infoValue}>{ticket.address.between_streets}</Typography>
                  </Box>
                </Grid>
              )}
              {ticket.address.references && (
                <Grid item xs={12} sm={6}>
                  <Box style={styles.infoSection}>
                    <Typography style={styles.infoLabel}>Referencias</Typography>
                    <Typography style={styles.infoValue}>{ticket.address.references}</Typography>
                  </Box>
                </Grid>
              )}
              {ticket.address.facade_description && (
                <Grid item xs={12} sm={6}>
                  <Box style={styles.infoSection}>
                    <Typography style={styles.infoLabel}>Descripción de Fachada</Typography>
                    <Typography style={styles.infoValue}>{ticket.address.facade_description}</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* {ticket.package_info && ticket.reportType.trim() !== 'Cambio de domicilio' && ticket.reportType.trim() !== 'Créditos' && ticket.reportType.trim() !== 'Página web' && ticket.reportType.trim() !== 'Retiro de efectivo' && ticket.reportType.trim() !== 'Agilizar entrega' && ticket.reportType.trim() !== 'Domicilio incorrecto' && ticket.reportType.trim() !== 'Servicio al cliente' && ( */}
        {ticket.package_info &&  (
          <Box style={styles.ticketMainInfoContainer}>
            {ticket.package_info && ticket.reportType.trim() !== 'Cambio de domicilio' && ticket.reportType.trim() !== 'Créditos' && ticket.reportType.trim() !== 'Página web' && ticket.reportType.trim() !== 'Retiro de efectivo' && ticket.reportType.trim() !== 'Agilizar entrega' && ticket.reportType.trim() !== 'Domicilio incorrecto' && ticket.reportType.trim() !== 'Servicio al cliente' && (
              <Typography style={styles.imagesSectionLabel}>Información del Paquete</Typography>
            )}
            <Grid container spacing={1} style={{ marginTop: 8 }}>
            {ticket.package_info && ticket.reportType.trim() !== 'Cambio de domicilio' && ticket.reportType.trim() !== 'Créditos' && ticket.reportType.trim() !== 'Página web' && ticket.reportType.trim() !== 'Retiro de efectivo' && ticket.reportType.trim() !== 'Agilizar entrega' && ticket.reportType.trim() !== 'Domicilio incorrecto' && ticket.reportType.trim() !== 'Servicio al cliente' && (
              <Grid item xs={12} sm={6}>
                <Box style={styles.infoSection}>
                  <Typography style={styles.infoLabel}>Contenido del Paquete</Typography>
                  <Typography style={styles.infoValue}>{ticket.package_info.package_content || "N/A"}</Typography>
                </Box>
              </Grid>
            )}
              {ticket.package_info && ticket.reportType.trim() !== 'Cambio de domicilio' && ticket.reportType.trim() !== 'Créditos' && ticket.reportType.trim() !== 'Página web' && ticket.reportType.trim() !== 'Retiro de efectivo' && ticket.reportType.trim() !== 'Agilizar entrega' && ticket.reportType.trim() !== 'Domicilio incorrecto' && ticket.reportType.trim() !== 'Servicio al cliente' && (
              <Grid item xs={12} sm={6}>
                <Box style={styles.infoSection}>
                  <Typography style={styles.infoLabel}>Tipo de Empaque</Typography>
                  <Typography style={styles.infoValue}>{ticket.package_info.packaging_type || "N/A"}</Typography>
                </Box>
              </Grid>
              )}
              {ticket.package_info.comments && (
                <Grid item xs={12}>
                  <Box style={styles.infoSection}>
                    <Typography style={styles.infoLabel}>Comentarios</Typography>
                    <Typography style={styles.infoValue}>{ticket.package_info.comments}</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {ticket.administratorResponse && (
          <Box style={styles.commentSection}>
            <Typography style={styles.commentLabel}>Comentario del Administrador</Typography>
            <Box style={styles.commentBox}>
              <Typography style={styles.commentValue}>{ticket.administratorResponse}</Typography>
            </Box>
          </Box>
        )}

        {hasImages && (
          <Box style={styles.imagesSection}>
            <Typography style={styles.imagesSectionLabel}>
              {ticket.reportType === "servicio_cliente" || ticket.reportType === "pagina_web"
                ? "Evidencia"
                : "Imágenes y Documentos"}
            </Typography>
            <Grid container spacing={2}>
              {(() => {
                const isSpecialReportType = ticket.reportType === "servicio_cliente" || ticket.reportType === "pagina_web";
                const imagesToShow = isSpecialReportType
                  ? Object.entries(ticket.images || {}).filter(([key]) => key === "front_image")
                  : Object.entries(ticket.images || {});

                return imagesToShow.map(([key, url]) => {
                  if (!url) return null;

                  const isPdf = key === "image_invoice_content" || url.toLowerCase().endsWith(".pdf");

                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <Card
                        elevation={1}
                        sx={styles.imageCard}
                        onClick={() => (isPdf ? handlePdfClick(url) : handleImageClick(url))}
                      >
                        {isPdf ? (
                          <Box style={styles.pdfContainer}>
                            <DocumentText1 size={48} variant="Bold" style={styles.pdfIcon} />
                          </Box>
                        ) : (
                          <CardMedia
                            component="img"
                            image={url}
                            alt={isSpecialReportType ? "Evidencia" : getImageLabel(key)}
                            sx={styles.cardMedia}
                          />
                        )}
                        <CardContent style={styles.imageCardContent}>
                          <Typography style={styles.imageCardTitle}>
                            {isSpecialReportType ? "Evidencia" : getImageLabel(key)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                });
              })()}
            </Grid>
          </Box>
        )}

        <ImageDialog
          open={imageDialogOpen}
          onClose={handleImageDialogClose}
          maxWidth={false}
          PaperProps={{ style: styles.imageDialog }}
        >
          {selectedImage && (
            <>
              <Box style={styles.imageDialogHeader}>
                <IconButton
                  onClick={() => handleDownloadImage(selectedImage, "evidencia.jpg")}
                  style={styles.downloadImageButton}
                  aria-label="Descargar"
                >
                  <DocumentDownload variant="Bold" size={24} />
                </IconButton>
                <IconButton
                  onClick={handleImageDialogClose}
                  style={styles.closeImageButton}
                  aria-label="Cerrar"
                >
                  <CloseCircle variant="Bold" size={24} />
                </IconButton>
              </Box>
              <Box style={styles.imageDialogContent}>
                <img
                  src={selectedImage}
                  alt="Vista ampliada"
                  style={styles.fullSizeImage}
                />
              </Box>
            </>
          )}
        </ImageDialog>

        <Container style={styles.buttonsContainer}>
          <Button
            variant="standard"
            color="primary"
            onClick={handleDialogClose}
            style={styles.cancelButton}
          >
            Cerrar
          </Button>
        </Container>
      </Container>
    </Dialog>
  );
};

export default TicketDetailsDialog;
